/* eslint-disable arrow-body-style */
import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import {
  Button,
  Dialog,
  Paper,
  Grid,
  IconButton,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  FormControl,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  DialogContent,
  Box,
  InputAdornment,
  TablePagination,
  Select,
  MenuItem,
  Avatar,
  InputLabel,
  Card,
  CardContent,
  Container,
  CardActions,
  CardHeader,
  Collapse,
  Badge,
} from '@mui/material';
import { useDebounce } from 'use-debounce';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import UIContext from '../../context/UIContext';
import axios from '../../utils/axios';

const Orders = () => {
  const { openSnackBar, showLoading, hideLoading } = useContext(UIContext);

  // State declarations
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [userId, setUserId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [debouncedProductSearch] = useDebounce(productSearch, 300);


  // Form initial values matching TBS_ORDER_HEADER_MST
  const [initialValues, setInitialValues] = useState({
    CustomerId: '',
    OrderStatus: 'Pending',
    Items: [],
    TotalAmount: 0,
    DeliveryCharge: 0,
    GrandTotal: 0,
    PaymentMode: 'COD',
    PaymentStatus: 'Pending',
    Remarks: '',
    Address1: '',
    Address2: '',
    City: '',
    State: '',
    Pincode: '',
  });

  // Calculate order totals matching database schema
  const calculateTotals = useCallback(
    (items) => {
      const totalAmount = items.reduce((sum, item) => sum + item.Rate * item.Quantity, 0);
      const totalAmountWithoutTax = items.reduce((sum, item) => {
        const base = item.Quantity * item.Rate;
        const discount = (base * (item.DiscPercent || 0)) / 100;
        const taxable = base - discount;
        const tax = (taxable * (item.Tax || 0)) / 100;
        const final = taxable + tax;

        return sum + final;
      }, 0);
      const deliveryCharge = initialValues.DeliveryCharge;
      const grandTotal = totalAmountWithoutTax + deliveryCharge;

      return { totalAmount, grandTotal };
    },
    [initialValues.DeliveryCharge]
  );

  // Update totals when items or delivery charge changes
  useEffect(() => {
    if (initialValues.Items.length > 0) {
      const { totalAmount, grandTotal } = calculateTotals(initialValues.Items);
      setInitialValues((prev) => ({
        ...prev,
        TotalAmount: totalAmount,
        GrandTotal: grandTotal,
      }));
    }
  }, [initialValues.Items, initialValues.DeliveryCharge, calculateTotals]);

  // Validation schema matching database constraints
  const validationSchema = Yup.object().shape({
    CustomerId: Yup.number().required('Customer is required'),
    OrderStatus: Yup.string().required('Status is required'),
    Items: Yup.array().min(1, 'At least one item is required'),
    TotalAmount: Yup.number().min(0).required(),
    DeliveryCharge: Yup.number().min(0).required(),
    GrandTotal: Yup.number().min(0).required(),
    PaymentMode: Yup.string().required('Payment method is required'),
    PaymentStatus: Yup.string().required('Payment status is required'),
    Address1: Yup.string().required('Address is required'),
    City: Yup.string().required('City is required'),
    State: Yup.string().required('State is required'),
    Pincode: Yup.string().required('Pincode is required'),
  });

  // Filter products based on search
  useEffect(() => {
    if (products.length > 0) {
      const filtered = products.filter(
        (product) =>
          product.Product_Details_Description.toLowerCase().includes(debouncedProductSearch.toLowerCase()) ||
          product.Product_Details_Code.toLowerCase().includes(debouncedProductSearch.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [products, debouncedProductSearch]);

  // Filtered orders
  const filteredOrders = useMemo(() => {
      return orders.filter((order) => {
      const matchesSearch =
        order.Order_Id.toString().includes(debouncedSearch) ||
        order.Customer_Name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        order.Payment_Status?.toLowerCase().includes(debouncedSearch.toLowerCase());
      const matchesStatus = statusFilter === 'all' || order.Order_Status === statusFilter;
      const matchesPayment = paymentFilter === 'all' || order.Payment_Status === paymentFilter;
      return matchesSearch && matchesStatus && matchesPayment;
    });
  }, [orders, debouncedSearch, statusFilter, paymentFilter]);
    console.log("filteredOrders",filteredOrders);
  // Data fetching
  const fetchData = useCallback(async () => {
    try {
      showLoading();
      const [ordersRes, productsRes, customersRes] = await Promise.all([
        axios.get('transaction/orders/GetAllOrders'),
        axios.get('Master/Products/GetAllProducts'),
        axios.get('Master/Customer/GetAllCustomer'),
      ]);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setCustomers(customersRes.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Failed to load data', 'error');
    } finally {
      hideLoading();
    }
  }, [showLoading, hideLoading, openSnackBar]);

  // Initialize data
  useEffect(() => {
    const UserId = sessionStorage.getItem('UserId');
    setUserId(UserId);
    fetchData();
  }, [fetchData]);

  // Handle product selection
  const handleAddProduct = (product) => {
    const existingItemIndex = initialValues.Items.findIndex((item) => item.ProductId === product.Product_Details_Id);

    if (existingItemIndex >= 0) {
      const updatedItems = [...initialValues.Items];
      updatedItems[existingItemIndex].Quantity += 1;
      updatedItems[existingItemIndex].TotalPrice =
        updatedItems[existingItemIndex].Rate * updatedItems[existingItemIndex].Quantity;
      setInitialValues((prev) => ({ ...prev, Items: updatedItems }));
    } else {
      setInitialValues((prev) => ({
        ...prev,
        Items: [
          ...prev.Items,
          {
            ProductId: product.Product_Details_Id,
            ProductName: product.Product_Details_Description,
            Rate: product.SellingPrice,
            Quantity: 1,
            TotalPrice: product.SellingPrice,
            Tax: product.TaxPercent || 0,
            DiscPercent: product.DiscPercent || 0,
          },
        ],
      }));
    }
    setOpenProductDialog(false);
    setProductSearch('');
  };

  const handleQuantityChange = (index, newQuantity) => {
    if (newQuantity < 1) return;
    setInitialValues((prev) => {
      const updatedItems = prev.Items.map((item, i) =>
        i === index
          ? {
              ...item,
              Quantity: newQuantity,
              TotalPrice: item.Rate * newQuantity,
            }
          : item
      );

      return {
        ...prev,
        Items: updatedItems,
      };
    });
  };

  // Remove product from order
  const handleRemoveProduct = (index) => {
    const updatedItems = [...initialValues.Items];
    updatedItems.splice(index, 1);
    setInitialValues((prev) => ({ ...prev, Items: updatedItems }));
  };

  const handleSubmit = useCallback(
    async (values) => {
      try {
        showLoading();
        console.log('Submitting with values:', values);
        console.log('Selected order:', selectedOrder);

        const orderData = {
          ...(edit && { OrderId: selectedOrder.Order_Id }),
          CustomerId: values.CustomerId,
          OrderStatus: values.OrderStatus,
          Items: values.Items.map((item) => ({
            ProductId: item.Product_Id,
            Quantity: item.Quantity,
            Rate: item.Rate,
            TotalPrice: item.TotalPrice,
            TaxPercent: item.Tax,
            DiscPercent: item.DiscPercent,
          })),
          TotalAmount: values.TotalAmount,
          DeliveryCharge: values.DeliveryCharge,
          GrandTotal: values.GrandTotal,
          PaymentMode: values.PaymentMode,
          PaymentStatus: values.PaymentStatus,
          PaidAmount: values.PaymentStatus === 'Paid' ? values.GrandTotal : 0,
          Remarks: values.Remarks,
          Address1: values.Address1,
          Address2: values.Address2,
          City: values.City,
          State: values.State,
          Pincode: values.Pincode,
          UserId: userId,
        };

        console.log('Sending order data:', orderData);

        if (edit) {
          await axios.put(`transaction/orders/UpdateOrder/${selectedOrder.Order_Id}`, orderData);
          openSnackBar('Order updated successfully!', 'success');
        } else {
          await axios.post('transaction/orders/CreateOrder', orderData);
          openSnackBar('Order created successfully!', 'success');
        }

        fetchData();
        handleCloseDialog();
      } catch (e) {
        console.error('Error:', e.response?.data);
        openSnackBar(e.response?.data?.message || 'Operation failed', 'error');
      } finally {
        hideLoading();
      }
    },
    [edit, selectedOrder, userId, fetchData, showLoading, hideLoading, openSnackBar]
  );

  // Status update
  const updateOrderStatus = useCallback(
    async (orderId, status) => {
      try {
        showLoading();
        await axios.patch(`transaction/orders/UpdateOrderStatus/${orderId}`, { OrderStatus: status });
        fetchData();
        openSnackBar('Order status updated successfully!', 'success');
      } catch (e) {
        openSnackBar(e.response?.data?.message || 'Update failed', 'error');
      } finally {
        hideLoading();
      }
    },
    [fetchData, showLoading, hideLoading, openSnackBar]
  );

  // Payment status update
  const updatePaymentStatus = useCallback(
    async (orderId, status) => {
      try {
        showLoading();
        await axios.patch(`transaction/orders/UpdatePaymentStatus/${orderId}`, { PaymentStatus: status });
        fetchData();
        openSnackBar('Payment status updated successfully!', 'success');
      } catch (e) {
        openSnackBar(e.response?.data?.message || 'Update failed', 'error');
      } finally {
        hideLoading();
      }
    },
    [fetchData, showLoading, hideLoading, openSnackBar]
  );

  // Delete order
  const deleteOrder = useCallback(async () => {
    try {
      showLoading();
      await axios.delete(`transaction/orders/DeleteOrder/${deleteId}`);
      fetchData();
      openSnackBar('Order deleted successfully!', 'success');
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Delete failed', 'error');
    } finally {
      hideLoading();
      handleCloseDelete();
    }
  }, [deleteId, fetchData, showLoading, hideLoading, openSnackBar]);

  // Edit order
  const handleEditClick = useCallback(
    async (order) => {
      try {
        showLoading();
        setSelectedOrder(order);
        console.log(order, 'order');
        const detailsResponse = await axios.get(`transaction/orders/GetOrderById/${order.Order_Id}`);
        const orderDetails = detailsResponse.data;
        console.log(orderDetails, 'orderDetails');
        setInitialValues({
          CustomerId: order.Customer_Id,
          OrderStatus: order.Order_Status,
          Items: orderDetails.items || [],
          TotalAmount: order.Total_Amount,
          DeliveryCharge: order.Delivery_Charge,
          GrandTotal: order.Grand_Total,
          PaymentMode: order.Payment_Mode === 'Cash on Delivery' ? 'COD' : 'Razorpay',
          PaymentStatus: order.Payment_Status,
          Remarks: order.Remarks || '',
          Address1: order.Address1,
          Address2: order.Address2 || '',
          City: order.City,
          State: order.State,
          Pincode: order.Pincode,
        });

        setEdit(true);
        setOpen(true);
      } catch (e) {
        openSnackBar('Failed to load order details', 'error');
      } finally {
        hideLoading();
      }
    },
    [showLoading, hideLoading, openSnackBar]
  );

  // Dialog handlers
  const handleCloseDialog = useCallback(() => {
    setOpen(false);
    setEdit(false);
    setSelectedOrder(null);
    setInitialValues({
      CustomerId: '',
      OrderStatus: 'Pending',
      Items: [],
      TotalAmount: 0,
      DeliveryCharge: 0,
      GrandTotal: 0,
      PaymentMode: 'COD',
      PaymentStatus: 'Pending',
      Remarks: '',
      Address1: '',
      Address2: '',
      City: '',
      State: '',
      Pincode: '',
    });
  }, []);

  const handleCloseDelete = useCallback(() => {
    setOpenDelete(false);
    setDeleteId(null);
  }, []);

  // Format date
  const formatDate = useCallback((dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }, []);

  // Format currency
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  }, []);

  const handleDeliveryChange = (e, setFieldValue, values) => {
    const delivery = parseFloat(e.target.value) || 0;
    const subtotal = parseFloat(values.TotalAmount) || 0;
    setFieldValue('DeliveryCharge', delivery);
    setFieldValue('GrandTotal', subtotal + delivery);
  };

  // Toggle order details expansion
  const handleExpandOrder = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Toggle edit mode for order status
  const toggleEditStatus = (order) => {
    setEditingOrder(editingOrder === order.Order_Id ? null : order.Order_Id);
  };

  // Handle status change in card view
  const handleStatusChange = async (orderId, field, value) => {
    if (field === 'OrderStatus') {
      await updateOrderStatus(orderId, value);
    } else if (field === 'PaymentStatus') {
      await updatePaymentStatus(orderId, value);
    }
    setEditingOrder(null);
  };

  // Calculate tax details for an order
  const calculateTaxDetails = (orderItems) => {
    const taxSummary = {};

    orderItems.forEach((item) => {
      const baseAmount = item.Quantity * item.Rate;
      const discountAmount = (baseAmount * (item.DiscPercent || 0)) / 100;
      const taxableAmount = baseAmount - discountAmount;
      const taxAmount = (taxableAmount * (item.Tax || 0)) / 100;

      if (!taxSummary[item.Tax]) {
        taxSummary[item.Tax] = {
          taxRate: item.Tax,
          taxableAmount: 0,
          taxAmount: 0,
        };
      }

      taxSummary[item.Tax].taxableAmount += taxableAmount;
      taxSummary[item.Tax].taxAmount += taxAmount;
    });

    return Object.values(taxSummary);
  };

  // Calculate item total with tax and discount
  const calculateItemTotal = (item) => {
    const baseAmount = item.Quantity * item.Rate;
    const discountAmount = (baseAmount * (item.DiscPercent || 0)) / 100; 
    const taxableAmount = baseAmount - discountAmount;
    const taxAmount = (taxableAmount * (item.Tax || 0)) / 100;
    return taxableAmount + taxAmount;
  };

    const calculateTaxTotal = (item) => {
    const baseAmount = item.Quantity * item.Rate;
    const discountAmount = (baseAmount * (item.DiscPercent || 0)) / 100; 
    const taxableAmount = baseAmount - discountAmount;
    const taxAmount = (taxableAmount * (item.Tax || 0)) / 100;
    return taxAmount;
  };

   const calculateDiscAmt = (item) => {
    const baseAmount = item.Quantity * item.Rate;
    const discountAmount = (baseAmount * (item.DiscPercent || 0)) / 100; 
    return discountAmount
  };
  const calculateTotalDiscount = (items) => {
  let totalDiscount = 0;

  items.forEach((item) => {
    const baseAmount = item.Quantity * item.Rate;
    const discountAmount = (baseAmount * (item.DiscPercent || 0)) / 100;
    totalDiscount += discountAmount;
  });

  return totalDiscount;
};
  // Order Card Component
  const OrderCard = React.memo(({ order, onEdit, onDelete }) => {
 const isExpanded = expandedOrder === order.Order_Id;
    const isEditing = editingOrder === order.Order_Id;
    const taxDetails = calculateTaxDetails(order.items || []);
    const itemCount = order.items ? order.items.reduce((sum, item) => sum + item.Quantity, 0) : 0;

    return (
      <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        <CardHeader
          avatar={
            <Badge badgeContent={itemCount} color="primary">
              <Avatar sx={{ bgcolor: 'primary.main' }}>{order.Customer_Name?.charAt(0)}</Avatar>
            </Badge>
          }
          action={
            <Box>
              <IconButton size="small" color="primary" onClick={() => onEdit(order)} sx={{ mr: 1 }}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setDeleteId(order.Order_Id);
                  setOpenDelete(true);
                }}
              >
                <DeleteForeverIcon fontSize="small" />
              </IconButton>
            </Box>
          }
          title={
            <Box display="flex" alignItems="center">
              <Typography variant="h6" fontWeight="bold">
                Order #{order.Order_Id}
              </Typography>
              <Chip
                label={order.Payment_Mode === 'Cash on Delivery' ? 'COD' : 'Online'}
                size="small"
                color="default"
                sx={{ ml: 1 }}
              />
            </Box>
          }
          subheader={
            <Box>
              <Typography variant="body2" color="textSecondary">
                {order.Customer_Name} • {formatDate(order.Order_Date)}
              </Typography>
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <LocalShippingIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Status
                </Typography>
              </Box>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={order.Order_Status}
                    onChange={(e) => handleStatusChange(order.Order_Id, 'OrderStatus', e.target.value)}
                  >
                    <MenuItem value="Order Placed">Order Placed</MenuItem>
                    <MenuItem value="Processing">Processing</MenuItem>
                    <MenuItem value="Shipped">Shipped</MenuItem>
                    <MenuItem value="Delivered">Delivered</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Box display="flex" alignItems="center">
                  <Chip
                    label={order.Order_Status}
                    size="small"
                    color={
                      order.Order_Status === 'Delivered'
                        ? 'success'
                        : order.Order_Status === 'Cancelled'
                        ? 'error'
                        : order.Order_Status === 'Processing'
                        ? 'warning'
                        : 'primary'
                    }
                  />
                  <IconButton size="small" onClick={() => toggleEditStatus(order)} sx={{ ml: 1 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" alignItems="center" mb={0.5}>
                <PaymentIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Payment
                </Typography>
              </Box>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={order.Payment_Status}
                    onChange={(e) => handleStatusChange(order.Order_Id, 'PaymentStatus', e.target.value)}
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Failed">Failed</MenuItem>
                    <MenuItem value="Refunded">Refunded</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Box display="flex" alignItems="center">
                  <Chip
                    label={order.Payment_Status}
                    size="small"
                    color={
                      order.Payment_Status === 'Paid'
                        ? 'success'
                        : order.Payment_Status === 'Failed' || order.Payment_Status === 'Refunded'
                        ? 'error'
                        : 'warning'
                    }
                  />
                  <IconButton size="small" onClick={() => toggleEditStatus(order)} sx={{ ml: 1 }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Total Amount
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {formatCurrency(order.Grand_Total)}
              </Typography>
            </Grid>
            {/* <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Items
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {itemCount} {itemCount === 1 ? 'item' : 'items'}
              </Typography>
            </Grid> */}
          </Grid>
        </CardContent>
        {/* 
        <CardActions disableSpacing sx={{ borderTop: '1px solid #f0f0f0', pt: 1 }}>
          <IconButton
            onClick={() => handleExpandOrder(order.Order_Id)}
            aria-expanded={isExpanded}
            aria-label="show more"
          >
            <ExpandMoreIcon />
          </IconButton>
          <Typography variant="body2">{isExpanded ? 'Hide Details' : 'Show Details'}</Typography>
        </CardActions> */}

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Order Items
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="center">Qty</TableCell>
                    <TableCell align="right">Discount %</TableCell>
                    <TableCell align="right">Tax %</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.items || []).map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {item.ProductName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            SKU: {item.Product_Code}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(item.Rate)}</TableCell>
                      <TableCell align="center">{item.Quantity}</TableCell>
                      <TableCell align="right">{item.DiscPercent || 0}%</TableCell>
                      <TableCell align="right">{item.Tax || 0}%</TableCell>
                      <TableCell align="right">{formatCurrency(calculateItemTotal(item))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {taxDetails.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Tax Summary
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Tax Rate</TableCell>
                        <TableCell align="right">Taxable Amount</TableCell>
                        <TableCell align="right">Tax Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {taxDetails.map((tax, index) => (
                        <TableRow key={index}>
                          <TableCell>{tax.taxRate}%</TableCell>
                          <TableCell align="right">{formatCurrency(tax.taxableAmount)}</TableCell>
                          <TableCell align="right">{formatCurrency(tax.taxAmount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2">Subtotal:</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="body2">{formatCurrency(order.Total_Amount)}</Typography>
                </Grid>

                {taxDetails.map((tax, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={6}>
                      <Typography variant="body2">Tax ({tax.taxRate}%):</Typography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="body2">{formatCurrency(tax.taxAmount)}</Typography>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid item xs={6}>
                  <Typography variant="body2">Delivery Charge:</Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="body2">{formatCurrency(order.Delivery_Charge)}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body1" fontWeight="bold">
                    Grand Total:
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(order.Grand_Total)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {order.Remarks && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Remarks: {order.Remarks}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <LocationOnIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Shipping Address</Typography>
              </Box>
              <Typography variant="body2">
                {order.Address1}, {order.Address2 && `${order.Address2}, `}
                {order.City}, {order.State} - {order.Pincode}
              </Typography>
            </Box>
          </CardContent>
        </Collapse>
      </Card>
    );
  });

  return (
    <Container maxWidth="xl" sx={{ pt: 2 }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{ p: 3, mb: 3, borderRadius: 3, background: 'linear-gradient(45deg, #f5f7fa, #e4e8f0)' }}
      >
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Order Management
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage and track all customer orders
            </Typography>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => setOpen(true)}
              startIcon={<PlaylistAddIcon />}
              sx={{ borderRadius: 2, boxShadow: 3 }}
            >
              Create Order
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Filters Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="medium"
              placeholder="Search orders by ID, customer, or payment status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel>Order Status</InputLabel>
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Order Status">
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Order Placed">Order Placed</MenuItem>
                <MenuItem value="Processing">Processing</MenuItem>
                <MenuItem value="Shipped">Shipped</MenuItem>
                <MenuItem value="Delivered">Delivered</MenuItem>
                <MenuItem value="Cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <FormControl fullWidth size="medium">
              <InputLabel>Payment Status</InputLabel>
              <Select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} label="Payment Status">
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Failed">Failed</MenuItem>
                <MenuItem value="Refunded">Refunded</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="textSecondary">
              Showing {filteredOrders.length} of {orders.length} orders
            </Typography>
            {filteredOrders.length === 0 && (
              <Button variant="outlined" size="small" onClick={fetchData} sx={{ mt: 1 }}>
                Refresh Data
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Orders Cards */}
      <Box>
        {filteredOrders.length > 0 ? (
          filteredOrders
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((order) => (
              <OrderCard key={order.Order_Id} order={order} onEdit={handleEditClick} onDelete={deleteOrder} />
            ))
        ) : (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6" color="textSecondary">
              No orders found
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Try adjusting your search or filter criteria
            </Typography>
          </Paper>
        )}
      </Box>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredOrders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        sx={{ mt: 2 }}
      />

      {/* Add/Edit Order Dialog */}
      <Dialog maxWidth="lg" open={open} onClose={handleCloseDialog} fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
          <Typography variant="h6" fontWeight="bold">
            {edit ? 'Edit Order' : 'Create Order'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
              <Form>
                <Grid container spacing={3} pt={1}>
                  {/* Customer Information (Read-only in edit mode) */}
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <PersonIcon color="action" sx={{ mr: 1 }} />
                        <Typography variant="h6">Customer Information</Typography>
                      </Box>
                      {edit ? (
                        <Typography variant="body1">
                          {customers.find((c) => c.CustomerId === values.CustomerId)?.FirstName || 'Customer'}(
                          {customers.find((c) => c.CustomerId === values.CustomerId)?.Mobile || 'N/A'})
                        </Typography>
                      ) : (
                        <FormControl fullWidth size="medium">
                          <InputLabel>Customer</InputLabel>
                          <Select
                            name="CustomerId"
                            value={values.CustomerId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={touched.CustomerId && !!errors.CustomerId}
                          >
                            {customers.map((customer) => (
                              <MenuItem key={customer.CustomerId} value={customer.CustomerId}>
                                {customer.FirstName} ({customer.Mobile})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Paper>
                  </Grid>

                  {/* Status Controls */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="medium">
                      <InputLabel>Order Status</InputLabel>
                      <Select name="OrderStatus" value={values.OrderStatus} onChange={handleChange} onBlur={handleBlur}>
                        <MenuItem value="Order Placed">Order Placed</MenuItem>
                        <MenuItem value="Processing">Processing</MenuItem>
                        <MenuItem value="Shipped">Shipped</MenuItem>
                        <MenuItem value="Delivered">Delivered</MenuItem>
                        <MenuItem value="Cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="medium">
                      <InputLabel>Payment Status</InputLabel>
                      <Select
                        name="PaymentStatus"
                        value={values.PaymentStatus}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={values.PaymentMode === 'COD' && !edit}
                      >
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Paid">Paid</MenuItem>
                        <MenuItem value="Failed">Failed</MenuItem>
                        <MenuItem value="Refunded">Refunded</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Order Items Section */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Order Items
                    </Typography>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0' }}
                    >
                      <Table size="medium" stickyHeader>
                        <TableHead>
                          <TableRow>
                          {/* <TableCell align="center">Product Code</TableCell> */}
                            <TableCell align="center">Product</TableCell>
                            <TableCell align="center">Rate</TableCell>
                            <TableCell align="center">Quantity</TableCell>
                            <TableCell align="center">Tax (%)</TableCell>
                            <TableCell align="center">Discount %</TableCell>
                            <TableCell align="center">Total</TableCell>
                            {!edit && <TableCell align="center">Actions</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {values.Items.map((item, index) => (
                            
                            <TableRow key={index} hover>
                              {/* <TableCell align="center">{item.Product_Code}</TableCell> */}
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {item.ProductName}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">{formatCurrency(item.Rate)}</TableCell>
                              <TableCell align="center">
                                {edit ? (
                                  <Typography>{item.Quantity}</Typography>
                                ) : (
                                  <Box display="flex" alignItems="center" justifyContent="center">
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setInitialValues(values);
                                        handleQuantityChange(index, item.Quantity - 1);
                                      }}
                                    >
                                      <RemoveIcon fontSize="small" />
                                    </IconButton>
                                    <Typography mx={1}>{item.Quantity}</Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        setInitialValues(values);
                                        handleQuantityChange(index, item.Quantity + 1);
                                      }}
                                    >
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}

                              </TableCell>
                              <TableCell align="center">{formatCurrency(calculateTaxTotal(item))} ({item.Tax}%)</TableCell>
                              <TableCell align="center">{formatCurrency(calculateDiscAmt(item))} ({item.DiscPercent}%)</TableCell>
                              <TableCell align="center">{formatCurrency(calculateItemTotal(item))}</TableCell>
                              {!edit && (
                                <TableCell align="center">
                                  <IconButton size="small" color="error" onClick={() => handleRemoveProduct(index)}>
                                    <DeleteForeverIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {!edit && (
                      <Button
                        variant="outlined"
                        size="medium"
                        sx={{ mt: 2, borderRadius: 2 }}
                        startIcon={<PlaylistAddIcon />}
                        onClick={() => setOpenProductDialog(true)}
                      >
                        Add Product
                      </Button>
                    )}
                  </Grid>

                  {/* Pricing Summary */}
                  <Grid item xs={12} sm={6}>
                    <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Pricing Summary
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography>Sub Total:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography>{formatCurrency(values.TotalAmount)}</Typography>
                        </Grid>

                         <Grid item xs={6}>
                          <Typography>Total Discount:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography>{formatCurrency(calculateTotalDiscount(values.Items))}</Typography>
                        </Grid>

                        {calculateTaxDetails(values.Items).map((tax, index) => (
                          <React.Fragment key={index}>
                            <Grid item xs={6}>
                              <Typography>Tax ({tax.taxRate}%):</Typography>
                            </Grid>
                            <Grid item xs={6} textAlign="right">
                              <Typography>{formatCurrency(tax.taxAmount)}</Typography>
                            </Grid>
                          </React.Fragment>
                        ))}

                        <Grid item xs={6}>
                          <Typography>Delivery Charges:</Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography>{values.DeliveryCharge}</Typography>
                          {/* <TextField
                            size="small"
                            type="number"
                            name="DeliveryCharge"
                            value={values.DeliveryCharge === 0 ? '' : values.DeliveryCharge}
                            onChange={(e) => handleDeliveryChange(e, setFieldValue, values)}
                            onBlur={handleBlur}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            sx={{ maxWidth: 100 }}
                            disabled={edit}
                          /> */}
                        </Grid>

                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>

                        <Grid item xs={6}>
                          <Typography variant="body1" fontWeight="bold">
                            Grand Total:
                          </Typography>
                        </Grid>
                        <Grid item xs={6} textAlign="right">
                          <Typography variant="body1" fontWeight="bold">
                            {formatCurrency(values.GrandTotal)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  {/* Shipping Address */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" gutterBottom>
                      Shipping Address
                    </Typography>
                    {edit ? (
                      <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="body1" paragraph>
                          {values.Address1}
                          {values.Address2 && `, ${values.Address2}`}
                        </Typography>
                        <Typography variant="body1">
                          {values.City}, {values.State} - {values.Pincode}
                        </Typography>
                        <Divider/>
                    <Box  mt={4}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <PaymentIcon fontSize="small" color="action" sx={{ mr: 1 }} />
                        <Typography variant="subtitle2" fontWeight="bold">Payment Mode</Typography>
                      </Box>
                     <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 12px',
                          borderRadius: 1,
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          backgroundColor: values.PaymentMode === 'COD' ? 'primary.main' : 'info.main',
                          color: 'white',
                          boxShadow: 1,
                        }}
                      >
                        {values.PaymentMode === 'COD' ? 'Cash on Delivery' : 'Razorpay'}
                      </Box>
                      {values.PaymentStatus && (
                        <Chip 
                          label={values.PaymentStatus} 
                          color={
                            values.PaymentStatus === 'Paid' ? 'success' : 
                            values.PaymentStatus === 'Pending' ? 'warning' : 'error'
                          }
                          variant="outlined"
                          size="small"
                          sx={{ 
                            ml: 1,
                            fontWeight: 'medium'
                          }}
                        />     )}
                        </Box>
                      </Paper>                      
                    ) : (
                      <>
                        <TextField
                          fullWidth
                          size="medium"
                          label="Address Line 1"
                          name="Address1"
                          value={values.Address1}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.Address1 && !!errors.Address1}
                          helperText={touched.Address1 && errors.Address1}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          size="medium"
                          label="Address Line 2"
                          name="Address2"
                          value={values.Address2}
                          onChange={handleChange}
                          sx={{ mb: 2 }}
                        />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="medium"
                              label="City"
                              name="City"
                              value={values.City}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.City && !!errors.City}
                              helperText={touched.City && errors.City}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="medium"
                              label="State"
                              name="State"
                              value={values.State}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.State && !!errors.State}
                              helperText={touched.State && errors.State}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="medium"
                              label="Pincode"
                              name="Pincode"
                              value={values.Pincode}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.Pincode && !!errors.Pincode}
                              helperText={touched.Pincode && errors.Pincode}
                            />
                          </Grid>
                        </Grid>
                      </>
                      
                    )}
                  </Grid>
                  

                  {/* Payment Mode (Read-only in edit mode) */}
                  {/* <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="medium">
                      <InputLabel>Payment Mode</InputLabel>
                      <Select
                        name="PaymentMode"
                        value={values.PaymentMode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={edit}
                      >
                        <MenuItem value="COD">Cash on Delivery</MenuItem>
                        <MenuItem value="Razorpay">Online Payment</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid> */}

                  {/* Remarks */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h6" gutterBottom>
                      Remarks
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Typography variant="body1" paragraph>
                        {values.Remarks}
                      </Typography>
                    </Paper>
                    {/*                       
                    <TextField
                      fullWidth
                      size="medium"
                      label="Remarks"
                      name="Remarks"
                      value={values.Remarks}
                      onChange={handleChange}
                      multiline
                      rows={2}
                    /> */}
                  </Grid>

                  {/* Dialog Actions */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                      <Button variant="outlined" color="secondary" onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color={edit ? 'warning' : 'primary'}
                        sx={{ borderRadius: 2 }}
                      >
                        {edit ? 'Update Order' : 'Create Order'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* Product Selection Dialog */}
      <Dialog
        open={openProductDialog}
        onClose={() => setOpenProductDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
          <Typography variant="h6" fontWeight="bold">
            Select Products
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            fullWidth
            size="medium"
            placeholder="Search products by name or code..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Grid container spacing={2}>
            {filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product.Product_Details_Id}>
                <Card
                  variant="outlined"
                  onClick={() => handleAddProduct(product)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover',
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom noWrap>
                      {product.Product_Details_Description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Code: {product.Product_Details_Code}
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight="bold" gutterBottom>
                      Price: {formatCurrency(product.SellingPrice)}
                    </Typography>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Tax: {product.TaxPercent || 0}%</Typography>
                      <Typography variant="body2">Stock: {product.Stock}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenProductDialog(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDelete}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
          <Typography variant="h6" fontWeight="bold">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2">
            Are you sure you want to delete this order? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDelete} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button color="error" onClick={deleteOrder} variant="contained" sx={{ borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Orders;
